/* eslint-disable react/no-array-index-key */
import { FC, useMemo } from 'react';
import { Modal } from 'react-bootstrap';

import { useTranslate } from 'hooks/useTranslate';

import { PartnerBulkUploadPayload } from '../../utils/formatPartnerForBulkUpload';
import { PARTNER_BULK_PREVIEW_COLUMNS } from '../../constants/partnerBulkPreviewColumns';
import { PartnerExcelDynamicColumns } from '../../utils/parsePartnerExcelDynamicColumns';

import './PartnersUploadReview.scss';

type Props = {
  partners: PartnerBulkUploadPayload[];
  totalPartnersInFile: number;
  dynamicExcelColumns?: PartnerExcelDynamicColumns | null;
  partnerCategories?: { _id?: string; name?: string }[];
  onBack: () => void;
  onConfirm: () => void;
  isConfirmDisabled?: boolean;
};

type MetricCardProps = {
  icon: string;
  iconBgClass: string;
  iconClass: string;
  label: string;
  value: number;
};

const MetricCard: FC<MetricCardProps> = ({
  icon,
  iconBgClass,
  iconClass,
  label,
  value,
}) => (
  <div className="card card-bordered h-100 partners-upload-review-metric-card">
    <div className="card-inner py-3">
      <div className={`partners-upload-review-metric-icon ${iconBgClass}`}>
        <em className={`icon ni ${icon} ${iconClass}`} />
      </div>
      <div>
        <span className="sub-text d-block mb-1">{label}</span>
        <span className="fs-18px fw-bold">{value}</span>
      </div>
    </div>
  </div>
);

type ColumnHeaderProps = {
  label: string;
  dbField: string;
};

const ColumnHeader: FC<ColumnHeaderProps> = ({ label, dbField }) => (
  <span className="partners-upload-review-column-header">
    <span className="text-uppercase d-block">{label}</span>
    <span className="text-lowercase text-soft fw-normal d-block">
      ({dbField})
    </span>
  </span>
);

const PartnersUploadReview: FC<Props> = ({
  partners,
  totalPartnersInFile,
  dynamicExcelColumns,
  partnerCategories = [],
  onBack,
  onConfirm,
  isConfirmDisabled = false,
}) => {
  const { t } = useTranslate();

  const summary = useMemo(() => {
    const categoriesCount = partners.reduce(
      (total, partner) => total + (partner.categories?.length || 0),
      0
    );
    const notesCount = partners.reduce(
      (total, partner) => total + (partner.notes?.length || 0),
      0
    );

    return {
      partnersCount: partners.length,
      categoriesCount,
      notesCount,
    };
  }, [partners]);

  const invalidPartners = useMemo(
    () => partners.filter((partner) => partner.hasValidationErrors),
    [partners]
  );

  const getCategoryName = (categoryId: string) => {
    const category = partnerCategories.find((item) => item?._id === categoryId);

    return category?.name || categoryId;
  };

  const dynamicCategoryColumns =
    dynamicExcelColumns?.categoryColumns?.map(({ categoryId }) => ({
      categoryId,
      label: getCategoryName(categoryId),
    })) || [];

  const hasDynamicNotes =
    (dynamicExcelColumns?.noteColumnKeys?.length || 0) > 0;

  return (
    <>
      <Modal.Header className="border-0 pb-0 d-flex justify-content-between">
        <div>
          <h5 className="modal-title">{t('PartnersUploadReviewTitle')}</h5>
          <span className="sub-text">
            {t('PartnersWillBeUploaded', {
              count: partners.length,
              total: totalPartnersInFile,
            })}
          </span>
        </div>
        <a className="close cursor-pointer" onClick={onBack}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>
      <Modal.Body className="partners-upload-review-body">
        <div className="partners-upload-review-summary">
          <div className="row mb-3">
            <div className="col-md-4">
              <MetricCard
                icon="ni-users"
                iconBgClass="bg-primary-dim"
                iconClass="text-primary"
                label={t('PartnersUploadSummaryPartnersLabel')}
                value={summary.partnersCount}
              />
            </div>
            <div className="col-md-4">
              <MetricCard
                icon="ni-layers"
                iconBgClass="bg-info-dim"
                iconClass="text-info"
                label={t('PartnersUploadSummaryCategoriesLabel')}
                value={summary.categoriesCount}
              />
            </div>
            <div className="col-md-4">
              <MetricCard
                icon="ni-notes-alt"
                iconBgClass="bg-warning-dim"
                iconClass="text-warning"
                label={t('PartnersUploadSummaryNotesLabel')}
                value={summary.notesCount}
              />
            </div>
          </div>

          {invalidPartners.length > 0 && (
            <div className="alert alert-danger mb-3" role="alert">
              <em className="icon ni ni-alert-circle mr-1" />
              {t('PartnersUploadReviewRowsWithErrors', {
                count: invalidPartners.length,
              })}
              {invalidPartners.length <= 5 && (
                <span className="d-block mt-1 sub-text">
                  {invalidPartners
                    .map((partner) => partner.cif || partner.name || '-')
                    .join(' | ')}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="nk-block partners-upload-review-table-wrap">
          <div className="card card-bordered">
            <div className="table-responsive">
              <table className="table table-tranx mb-0">
                <thead>
                  <tr className="tb-tnx-head">
                    <th className="nk-tb-col p-2">
                      <span>#</span>
                    </th>
                    {PARTNER_BULK_PREVIEW_COLUMNS.map((column) => (
                      <th key={column.dbField} className="nk-tb-col p-2">
                        <ColumnHeader
                          label={t(column.labelKey)}
                          dbField={column.dbField}
                        />
                      </th>
                    ))}
                    {dynamicCategoryColumns.map(({ categoryId, label }) => (
                      <th
                        key={`category-${categoryId}`}
                        className="nk-tb-col p-2"
                      >
                        <ColumnHeader
                          label={label}
                          dbField={`categories.${categoryId}`}
                        />
                      </th>
                    ))}
                    {hasDynamicNotes && (
                      <th className="nk-tb-col p-2">
                        <ColumnHeader label={t('Notes')} dbField="notes" />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner, index) => {
                    const rowClassName = partner.hasValidationErrors
                      ? 'table-danger'
                      : '';

                    return (
                      <tr
                        className={`tb-tnx-item ${rowClassName}`}
                        key={`${partner.cif}-${index}`}
                      >
                        <td className="tb-tnx-id py-1">
                          <span>{index + 1}</span>
                          {partner.hasValidationErrors && (
                            <em
                              className="icon ni ni-alert-circle text-danger ml-1"
                              title={partner.validationErrors?.join(' | ')}
                            />
                          )}
                        </td>
                        {PARTNER_BULK_PREVIEW_COLUMNS.map((column) => (
                          <td key={column.dbField} className="tb-tnx-info py-1">
                            <span className="tb-tnx-info">
                              {column.getValue(partner)}
                              {column.dbField === 'email' &&
                                partner.isGeneratedEmail && (
                                  <em
                                    className="icon ni ni-info-fill text-warning ml-1"
                                    title={t('GeneratedEmailIndicator')}
                                  />
                                )}
                            </span>
                          </td>
                        ))}
                        {dynamicCategoryColumns.map(({ categoryId }) => (
                          <td
                            key={`${partner.cif}-${categoryId}`}
                            className="tb-tnx-info py-1"
                          >
                            <span>
                              {partner.categoryAssignments?.[categoryId]
                                ? 'SI'
                                : '-'}
                            </span>
                          </td>
                        ))}
                        {hasDynamicNotes && (
                          <td className="tb-tnx-info py-1">
                            <span>
                              {partner.notes?.length
                                ? partner.notes.join('; ')
                                : '-'}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light d-flex justify-content-between">
        <button
          type="button"
          className="btn btn-outline-light"
          onClick={onBack}
        >
          {t('BackToUpload')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onConfirm}
          disabled={isConfirmDisabled || partners.length === 0}
          data-testid="confirm-upload-partners-button"
        >
          {t('ConfirmUploadPartners')}
        </button>
      </Modal.Footer>
    </>
  );
};

export default PartnersUploadReview;
